import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import readableFileSize from 'filesize';
import { Url } from '@wix/da-url';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { CoreLevel } from '@wix/da-react-context/pkg/publicSession';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
// import CoreSymbol from '@wix/da-ds/pkg/Indicators/CoreSymbol';
import ProgressBar from '@wix/da-shared-react/pkg/ProgressBar';
import { STORAGE_CAPACITY_LOW_THRESHOLD } from '../constants';

import s from './StorageCapacityWithUpsell.scss';

export interface Props {
  coreLevel: CoreLevel;
  maxCapacity: number;
  freeCapacity: number;
  className?: string;
}

const StorageCapacityWithUpsell: FC<Props> = ({
  className,
  coreLevel,
  maxCapacity,
  freeCapacity,
}) => {
  const { t } = useTranslation();
  const hasLowCapacity = freeCapacity <= STORAGE_CAPACITY_LOW_THRESHOLD;
  const usedCapacity = maxCapacity - freeCapacity;
  const percentage = maxCapacity ? (usedCapacity / maxCapacity) * 100 : 100;
  const canUpgradeCapacity = !coreLevel || coreLevel === 'core_access';

  return (
    <div className={cn(s['root'], className)}>
      <ProgressBar
        className={s['progressbar']}
        barClassName={s['progressbar-bar']}
        aria-label={t('studio.storageCapacityWithUpsell.progressBarAriaLabel')}
        percentage={percentage}
        background="var(--g-stroke-default)"
        color={hasLowCapacity ? 'var(--gradient7)' : 'var(--g-typography-secondary)'}
      />

      <div className={s['used-vs-max']}>
        {t('studio.storageCapacityWithUpsell.usedVsMax', {
          used: readableFileSize(Math.max(0, usedCapacity)),
          max: readableFileSize(maxCapacity),
        })}
      </div>

      {canUpgradeCapacity && (
        <TextButton
          className={s['upgrade-link']}
          size="small"
          variant="core-orange"
          href={Url.coreMembershipLink()}
          target="_blank"
          biData={BiData<BuyCoreButtonBiEvent>({
            evid: 375,
            referral_info: ReferralType.STUDIO_SIZE_LIMIT_TOOLTIP,
          })}
        >
          <span className={s['upgrade-link-text']}>
            {t('studio.storageCapacityWithUpsell.upgradeLink')}
          </span>
        </TextButton>
      )}
    </div>
  );
};
StorageCapacityWithUpsell.displayName = 'StorageCapacityWithUpsell';

export default StorageCapacityWithUpsell;
